/* Copilot generic control layer — "eyes + hands" for every interactive element.
 *
 * scanActionable() runs each message: it finds the visible interactive controls
 * on the current page, gives each a fresh per-message ref (c1, c2…), and returns
 * a compact list the AI receives. The generic client actions (clickControl /
 * setSelect / setValue / goToPage) then act on those refs. Refs are regenerated
 * every message, so the AI always acts on the CURRENT view — if the DOM changed,
 * resolveRef returns null and the action safely no-ops instead of clicking the
 * wrong thing.
 *
 * This is intentionally general: no per-page catalog to drift. Any button, link,
 * tab, filter, select, checkbox, row, or pagination control the operator can see
 * becomes controllable. */

import { setReactValue } from "./spotlight";

const SEL =
  'button, a[href], [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"], [role="link"], input:not([type="hidden"]), select, textarea, summary, [contenteditable="true"], .ql-editor, [tabindex]:not([tabindex="-1"])';

// ref -> WeakRef(el), rebuilt every scan so refs never outlive their message.
let CURRENT_REFS = new Map();

function isVisible(el) {
  if (!el || el.nodeType !== 1) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === "hidden" || style.display === "none") return false;
  if (el.offsetParent === null && style.position !== "fixed") return false;
  const r = el.getBoundingClientRect();
  return r.width > 1 && r.height > 1;
}

function isEditable(el) {
  return (
    el.isContentEditable ||
    el.getAttribute("contenteditable") === "true" ||
    (el.classList && el.classList.contains("ql-editor"))
  );
}

function labelFor(el) {
  const aria = el.getAttribute && el.getAttribute("aria-label");
  const title = el.getAttribute && el.getAttribute("title");
  const ph = el.getAttribute && el.getAttribute("placeholder");
  const dph = el.getAttribute && el.getAttribute("data-placeholder"); // Quill/CE
  const txt = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
  // For a rich-text editor prefer its placeholder over any typed content.
  const raw = isEditable(el)
    ? aria || dph || ph || txt || "text editor"
    : aria || title || txt || ph || dph || "";
  return raw.slice(0, 80);
}

function paginationBar(el) {
  return el.closest('[data-copilot="pagination-bar"]');
}

function kindFor(el) {
  const tag = el.tagName;
  if (isEditable(el)) return "input"; // rich-text / contenteditable
  if (tag === "SELECT") {
    const opts = Array.from(el.options || []).map((o) => o.value).join(",");
    if (paginationBar(el) || opts === "5,10,20,50") return "page-size";
    return "select";
  }
  if (tag === "INPUT") {
    const t = (el.getAttribute("type") || "text").toLowerCase();
    if (t === "checkbox") return "checkbox";
    if (t === "radio") return "radio";
    return "input";
  }
  if (tag === "TEXTAREA") return "input";
  const role = el.getAttribute("role");
  const aria = el.getAttribute("aria-label") || "";
  if (paginationBar(el)) return "pagination";
  if (/(Previous|Next) page/i.test(aria)) return "pagination";
  if (role === "tab" || (el.getAttribute("data-tour") || "").endsWith("-lane"))
    return "tab";
  if (isRow(el)) return "row-open";
  // A button/link INSIDE a list row (open/view/edit/⋮/tag) is a row action.
  if (
    el.closest('[data-tour="eq-subject-list"], [data-tour="mq-query-list"]') &&
    !isRow(el)
  )
    return "row-action";
  if (/^Filter:/.test((el.innerText || "").trim())) return "filter";
  if (tag === "SEARCH" || (el.getAttribute && el.getAttribute("type") === "search"))
    return "search";
  if (role === "menuitem" || role === "option") return "menuitem";
  if (tag === "A" || role === "link") return "link";
  return "button";
}

function isRow(el) {
  const inList = el.closest(
    '[data-tour="eq-subject-list"], [data-tour="mq-query-list"]'
  );
  if (!inList) return false;
  return el.getAttribute("role") === "button" || el.tagName === "TR";
}

function groupFor(el) {
  if (el.closest('[role="dialog"], [aria-modal="true"]')) return "dialog";
  if (el.closest('[data-copilot="pagination-bar"]')) return "pagination";
  if (el.closest('[data-tour="eq-search-bar"]')) return "search-bar";
  if (el.closest('[data-tour="eq-subject-list"], [data-tour="mq-query-list"]'))
    return "list";
  if (el.closest('[data-tour="qd-workspace-lanes"]')) return "lanes";
  if (el.closest('[role="tablist"], [data-tour="qd-right-panel"]'))
    return "inspector";
  if (el.closest('nav, aside, [data-tour^="sidebar"]')) return "sidebar";
  if (el.closest("header, [data-tour^='qd-header']")) return "header";
  return undefined;
}

// Synthesize a short label for an unlabelled control (icon-only buttons, etc.)
// so the AI can still address it. Pull from a nearby icon's data-icon/aria, then
// name/id/data-* attrs, then a bare positional hint. Kept short — these are
// budget-capped by the caller so token cost never explodes.
function synthLabel(el) {
  try {
    const icon = el.querySelector("svg[data-icon], [data-icon], i[class*='fa-']");
    if (icon) {
      const di =
        icon.getAttribute("data-icon") ||
        (icon.getAttribute("aria-label") || "").trim();
      if (di) return `${di.replace(/-/g, " ")} button`.slice(0, 40);
      const cls = icon.getAttribute("class") || "";
      const fa = cls.match(/fa-([a-z0-9-]+)/i);
      if (fa && fa[1]) return `${fa[1].replace(/-/g, " ")} button`.slice(0, 40);
    }
    const named =
      el.getAttribute("name") ||
      el.getAttribute("data-testid") ||
      el.getAttribute("data-tour") ||
      el.getAttribute("id");
    if (named) return String(named).replace(/[-_]/g, " ").slice(0, 40);
  } catch {
    /* */
  }
  return "icon button";
}

/**
 * @returns {Array<{ref,kind,label,value?,group?,disabled?,active?}>}
 */
export function scanActionable() {
  CURRENT_REFS = new Map();
  try {
    // Clear stamps from prior scans so the attribute fallback in resolveRef can
    // never resolve a recycled ref (c5…) to a stale element from a past view.
    document
      .querySelectorAll("[data-copilot-ref]")
      .forEach((el) => {
        try {
          delete el.dataset.copilotRef;
        } catch {
          /* readonly */
        }
      });
    // Scan the WHOLE document so the sidebar, header, and pop-up modals (which
    // portal to <body>, outside <main>) are all controllable — excluding only
    // the copilot's own panel/FAB (data-copilot-root).
    let els = Array.from(document.body.querySelectorAll(SEL));
    els = els.filter(
      (el) => !el.closest("[data-copilot-root]") && isVisible(el)
    );
    // When a modal/dialog is open, restrict to it (+ the sidebar) so the AI
    // operates the dialog rather than the greyed-out page behind it.
    const openDialog = Array.from(
      document.querySelectorAll('[role="dialog"], [aria-modal="true"]')
    ).find((d) => !d.closest("[data-copilot-root]") && isVisible(d));
    if (openDialog) {
      els = els.filter(
        (el) => openDialog.contains(el) || el.closest('[data-tour="sidebar-nav"], nav, header')
      );
    }
    // Keep innermost interactive elements, EXCEPT list rows (keep outermost row)
    // and rich-text editors (keep the editor itself, not links typed inside it).
    els = els.filter((el) => {
      if (isRow(el)) return !el.parentElement?.closest('[role="button"]');
      if (isEditable(el)) return true;
      // skip if it wraps another actionable element (it's a container)
      const inner = el.querySelector(SEL);
      if (inner && isVisible(inner)) return false;
      return true;
    });

    // Priority so the cap never drops the important stuff (editors, inputs,
    // pagination, rows) in favour of a wall of toolbar buttons.
    const PRIO = {
      input: 0,
      search: 0,
      "row-open": 1,
      pagination: 1,
      "page-size": 1,
      select: 2,
      filter: 2,
      tab: 2,
      checkbox: 3,
      "row-action": 3,
      menuitem: 3,
      link: 4,
      button: 5,
    };
    // 40 matches the backend's slice — anything above it was scanned,
    // serialized, and uploaded only to be dropped server-side.
    const CAP = 40;
    const ranked = els
      .map((el) => ({ el, kind: kindFor(el) }))
      .sort((a, b) => (PRIO[a.kind] ?? 5) - (PRIO[b.kind] ?? 5));

    const out = [];
    let n = 0;
    let rowIndex = 0;
    let synthCount = 0; // budget for synthesized icon-only labels
    const SYNTH_CAP = 10;
    for (const { el, kind } of ranked) {
      if (out.length >= CAP) break;
      let label = labelFor(el);
      if (kind === "row-open") {
        rowIndex += 1;
        // Rows are matched by their "N." prefix; the full text is already in
        // the snapshot's VISIBLE LIST — a long label here is paid twice.
        label = `${rowIndex}. ${(label || "row").slice(0, 48)}`;
      } else if (kind === "button" || kind === "link" || kind === "row-action") {
        // Chrome/action labels don't need 80 chars to be clickable-by-name,
        // and the safety regexes match well within 48.
        label = label.slice(0, 48);
      }
      if (!label) {
        // Unlabelled non-row control (icon-only button, etc.). Synthesize a
        // short handle so the AI can still operate it — capped so a toolbar of
        // nameless icons can't blow the token budget.
        if (synthCount >= SYNTH_CAP) continue;
        label = synthLabel(el);
        if (!label) continue;
        synthCount += 1;
      }
      n += 1;
      const ref = "c" + n;
      try {
        el.dataset.copilotRef = ref;
      } catch {
        /* readonly node */
      }
      CURRENT_REFS.set(ref, typeof WeakRef !== "undefined" ? new WeakRef(el) : el);
      const item = { ref, kind, label };
      if (el.tagName === "SELECT") item.value = el.value;
      else if (el.getAttribute("type") === "checkbox") item.value = !!el.checked;
      else if (kind === "pagination" && /^\d+$/.test((el.innerText || "").trim()))
        item.value = Number((el.innerText || "").trim());
      // (No `openable` field: the backend line-encoder never emitted it, so it
      // was pure upload weight — kind "row-open" already implies it, and
      // detailPathForRef reads data-copilot-open from the DOM at click time.)
      if (
        el.getAttribute("data-copilot-selected") === "true" ||
        el.getAttribute("aria-selected") === "true" ||
        el.getAttribute("aria-current") ||
        /!bg-green|bg-green-500/.test(el.className || "")
      )
        item.active = true;
      if (el.disabled || el.getAttribute("aria-disabled") === "true")
        item.disabled = true;
      const group = groupFor(el);
      if (group) item.group = group;
      out.push(item);
    }
    // Present in visual order (row-open sequence preserved via label prefix).
    return out;
  } catch {
    return [];
  }
}

function resolveRef(ref) {
  const stored = CURRENT_REFS.get(ref);
  const el = stored && stored.deref ? stored.deref() : stored;
  if (el && el.isConnected && isVisible(el)) return el;
  try {
    const byAttr = document.querySelector(
      `[data-copilot-ref="${CSS.escape(String(ref))}"]`
    );
    if (byAttr && byAttr.isConnected) return byAttr;
  } catch {
    /* bad ref */
  }
  return null;
}

function feedback(el) {
  try {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  } catch {
    /* jsdom */
  }
}

// Two-tier client-side safety (prompt-injection-proof — labels come from the
// element itself). HARD = irreversible/outward, never done by the AI. COMMIT =
// saves/sends/submits/mutating changes — allowed ONLY behind a ConfirmDialog
// the operator taps. Everything else is a safe UI action and runs directly.
// (Read-only verbs like print/export/download are NOT blocked.)
const HARD_DESTRUCTIVE =
  /\b(delete|del|remove|discard|archive|void|log\s?out|sign\s?out|logout|pay|payment|purchase|buy|checkout|deactivate|disable|unsubscribe|wipe|clear all|delete all)\b/i;
const COMMIT_LABEL =
  /\b(send|resend|submit|save|issue|approve|reject|finali[sz]e|publish|place order|book now|request rate|request rates)\b/i;

// Selects whose change mutates records (vs. a view filter / page size).
const MUTATING_SELECT = /\b(status|stage|assign|owner|operator|priority|state|role)\b/i;

function labelSummaryFor(el) {
  return (labelFor(el) || "this control").slice(0, 80);
}

/** 'hard' (never), 'commit' (confirm first), or null (safe to run). */
function safetyTier(el) {
  const label = labelFor(el) || "";
  if (HARD_DESTRUCTIVE.test(label)) return "hard";
  if ((el.getAttribute("type") || "").toLowerCase() === "submit") return "commit";
  if (COMMIT_LABEL.test(label)) return "commit";
  return null;
}

function refuse(label) {
  return {
    ok: false,
    blocked: true,
    label: `won't do “${label}” automatically — please do that one yourself`,
  };
}

// Perform a click bypassing the guard (after the operator confirms).
export function forceClick(ref) {
  const el = resolveRef(ref);
  if (!el) return { ok: false, label: "that control isn't on screen" };
  feedback(el);
  try {
    el.click();
  } catch {
    return { ok: false, label: "couldn't click it" };
  }
  return { ok: true, label: labelFor(el) || "done" };
}

/** The full-detail app path for a row ref (or its openable ancestor), if any. */
export function detailPathForRef(ref) {
  const el = resolveRef(ref);
  if (!el) return null;
  return (
    el.getAttribute("data-copilot-open") ||
    el.closest("[data-copilot-open]")?.getAttribute("data-copilot-open") ||
    null
  );
}

/** Click a control by ref. Safe controls run; commits ask for confirmation;
 *  hard-destructive controls are refused. */
export function clickControl(ref) {
  const el = resolveRef(ref);
  if (!el) return { ok: false, label: "that control isn't on screen" };
  const tier = safetyTier(el);
  if (tier === "hard") return refuse(labelSummaryFor(el));
  if (tier === "commit") {
    return {
      needsConfirm: true,
      confirm: {
        kind: "click",
        ref,
        summary: `Click “${labelSummaryFor(el)}”?`,
        confirmLabel: labelSummaryFor(el),
      },
      label: `confirm “${labelSummaryFor(el)}”`,
    };
  }
  // Re-assert the control is still visible + enabled at click time (the DOM may
  // have shifted since the scan) so we never report a click on a dead control.
  if (!isVisible(el) || el.disabled || el.getAttribute("aria-disabled") === "true")
    return { ok: false, label: "that control is disabled or off-screen" };
  feedback(el);
  try {
    el.click();
  } catch {
    return { ok: false, label: "couldn't click it" };
  }
  return { ok: true, label: labelFor(el) || "clicked" };
}

// Perform a select bypassing the mutating guard (after the operator confirms).
// ASYNC: resolves only once the value is set on a native <select>, or the
// matching option has actually been clicked in a custom dropdown — else ok:false
// when the poll budget is exhausted (no optimistic early success).
export async function forceSet(ref, value) {
  const el = resolveRef(ref);
  if (!el) return { ok: false, label: "that control isn't on screen" };
  if (el.tagName === "SELECT") {
    const v = String(value);
    const match = Array.from(el.options).find(
      (o) => o.value === v || o.text.trim() === v
    );
    el.value = match ? match.value : v;
    el.dispatchEvent(new Event("change", { bubbles: true }));
    return { ok: true, label: `set ${labelFor(el) || "option"} to ${value}` };
  }
  feedback(el);
  try {
    el.click();
  } catch {
    /* */
  }
  // Poll for the option to appear (menus render async) — up to ~1.6s. Resolve
  // ok:true only after actually clicking the option, ok:false if it never showed.
  const wanted = String(value).toLowerCase();
  for (let tries = 0; tries < 9; tries += 1) {
    const opt = Array.from(
      document.querySelectorAll('button, [role="option"], li, [role="menuitem"]')
    ).find((b) => {
      if (b.offsetParent === null) return false;
      const t = (b.innerText || "").trim().toLowerCase();
      return t === wanted || t.includes(wanted);
    });
    if (opt) {
      opt.click();
      return { ok: true, label: `selected ${value}` };
    }
    await delay(180);
  }
  return { ok: false, label: `couldn't find the “${value}” option` };
}

/** Set a <select>/dropdown. View controls (page size, filter) run directly;
 *  record-mutating selects (status/stage/assignee) confirm first. */
export async function setSelect(ref, value) {
  const el = resolveRef(ref);
  if (!el) return { ok: false, label: "that control isn't on screen" };
  const selLabel = labelFor(el) || "";
  if (HARD_DESTRUCTIVE.test(selLabel)) return refuse(selLabel);
  if (MUTATING_SELECT.test(selLabel) || COMMIT_LABEL.test(selLabel)) {
    return {
      needsConfirm: true,
      confirm: {
        kind: "set",
        ref,
        value: String(value ?? ""),
        summary: `Set “${labelSummaryFor(el)}” to “${value}”?`,
        confirmLabel: "Apply",
      },
      label: `confirm set “${labelSummaryFor(el)}”`,
    };
  }
  return forceSet(ref, value);
}

// Find the ReactQuill instance for an editor node. Quill stores itself on the
// .ql-container element as `__quill`, reachable by walking up a few levels.
function quillInstanceFor(el) {
  let n = el;
  for (let i = 0; i < 5 && n; i += 1) {
    if (n.__quill) return n.__quill;
    n = n.parentElement;
  }
  return null;
}

// Insert text into a contenteditable / rich-text editor. For ReactQuill we drive
// its own API (quill.insertText) — this updates Quill's model and fires the
// React onChange, and crucially works WITHOUT focus (the copilot panel holds
// focus, so execCommand('insertText') silently fails). Plain contenteditable
// falls back to execCommand.
function insertIntoEditable(el, text) {
  const q = quillInstanceFor(el);
  if (q && typeof q.insertText === "function") {
    try {
      const len = typeof q.getLength === "function" ? q.getLength() : 1;
      const at = Math.max(0, len - 1); // before Quill's trailing newline
      q.insertText(at, text, "user");
      try {
        q.setSelection(at + text.length, 0);
      } catch {
        /* selection optional */
      }
      return;
    } catch {
      /* fall through to generic path */
    }
  }
  try {
    el.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(el);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch {
    /* selection API unavailable */
  }
  let done = false;
  try {
    done = document.execCommand("insertText", false, text);
  } catch {
    done = false;
  }
  if (!done || !((el.innerText || el.textContent || "").includes(text))) {
    try {
      el.textContent = text;
      el.dispatchEvent(
        new InputEvent("input", { bubbles: true, inputType: "insertText", data: text })
      );
    } catch {
      /* older browser */
    }
  }
}

// A composer expand/re-render can wipe a just-inserted reply (the Quill
// instance remounts). Check shortly after and re-insert if our text vanished.
function verifyEditableInsert(text, tries = 0) {
  if (tries >= 3) return;
  const probe = String(text).slice(0, 40);
  setTimeout(() => {
    const ed = visibleReplyEditor();
    if (!ed) return;
    if (((ed.innerText || "").trim()).includes(probe)) return; // persisted
    insertIntoEditable(ed, text);
    verifyEditableInsert(text, tries + 1);
  }, 800);
}

function isField(el) {
  if (!el) return false;
  if (isEditable(el)) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA";
}

function visibleReplyEditor() {
  const eds = Array.from(
    document.querySelectorAll('.ql-editor, [contenteditable="true"]')
  ).filter((ed) => !ed.closest("[data-copilot-root]") && isVisible(ed));
  return eds[0] || null;
}

// The page's visible search/text input — used to recover when a setValue ref is
// STALE (e.g. a navigateThen step carried a ref scanned on the previous page,
// so it no longer resolves after navigation). Prefer a search-labelled input,
// else the first visible non-copilot text input.
function visibleSearchInput() {
  const inputs = Array.from(
    document.querySelectorAll('input:not([type="hidden"]), textarea')
  ).filter((el) => {
    if (el.closest("[data-copilot-root]")) return false;
    const t = (el.getAttribute("type") || "text").toLowerCase();
    if (["checkbox", "radio", "range", "file", "submit", "button"].includes(t))
      return false;
    if (el.disabled || el.readOnly) return false;
    return isVisible(el);
  });
  const searchish = inputs.find((el) => {
    const t = (el.getAttribute("type") || "").toLowerCase();
    const ph = (el.getAttribute("placeholder") || "").toLowerCase();
    const aria = (el.getAttribute("aria-label") || "").toLowerCase();
    return t === "search" || /search|filter|find/.test(ph + " " + aria);
  });
  return searchish || inputs[0] || null;
}

// Click a collapsed reply composer's "Expand to write a reply" trigger.
function openReplyComposer() {
  const matches = Array.from(
    document.querySelectorAll('[role="button"], button')
  ).filter(
    (n) =>
      !n.closest("[data-copilot-root]") &&
      n.offsetParent !== null &&
      /expand to write a reply/i.test((n.innerText || "").trim())
  );
  // An ancestor wrapper can match too ("ReplyExpand to write a reply…"), but
  // clicking it never fires the inner trigger's handler — React events only
  // bubble up. Click the DEEPEST match: prefer a node no other match contains.
  const trigger =
    matches.find((n) => !matches.some((m) => m !== n && n.contains(m))) || null;
  if (trigger) {
    try {
      trigger.click();
      return true;
    } catch {
      /* */
    }
  }
  return false;
}

const rAF = () =>
  new Promise((r) =>
    typeof requestAnimationFrame === "function"
      ? requestAnimationFrame(() => r())
      : setTimeout(r, 16)
  );
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/** Type into an input/search/textarea OR a rich-text editor by ref.
 *  For a reply/message it self-manages the composer: finds the visible reply
 *  editor, opening it if collapsed, then inserts via the Quill API.
 *  opts.isReply forces the reply path (the model states its intent — no
 *  guessing from labels/length, which misfires on short messages).
 *
 *  ASYNC + verify-after-act: the returned Promise resolves ONLY at the terminal
 *  state — text verified in the field, or the retry budget exhausted (ok:false).
 *  No mid-retry optimistic {ok:true}: the chip must reflect reality. */
export async function setValue(ref, value, opts = {}) {
  const { isReply = false } = opts;
  let el = resolveRef(ref);
  const v = String(value ?? "");
  // Stale-ref recovery: a navigateThen/sequence step's ref was scanned on the
  // PREVIOUS page, so after navigation it no longer resolves. If this isn't a
  // reply and the value is short (a search term, not a message), target the
  // page's actual visible search input instead of falling through to the reply
  // composer. This makes "go to Airlines and search Visual" fill the real box.
  if (!el && !isReply && v.length <= 80) {
    el = visibleSearchInput();
  }
  const label = el ? (labelFor(el) || "").toLowerCase() : "";
  // Treat as a reply/message when the ref IS a rich editor, its label reads like
  // one, the ref resolved to nothing (maybe an editor that wasn't scanned yet),
  // OR the value is clearly a message AND we're not aimed at a search/filter box
  // (so a long search term is never dumped into the reply editor).
  const isSearchish =
    el &&
    (el.tagName === "INPUT" || el.tagName === "SELECT") &&
    (/search|filter|rows per|page|to\b|cc\b|recipient/i.test(label) ||
      (el.getAttribute && el.getAttribute("type") === "search"));
  const replyIntent =
    isReply ||
    (el && isEditable(el)) ||
    /reply|message|write your/.test(label) ||
    !isField(el) ||
    (v.length > 80 && !isSearchish);
  if (replyIntent) {
    // Resolve the reply composer to a visible editor, opening it if collapsed.
    // Loop with SHORT settles until the Quill editor mounts or the budget runs
    // out — resolving false-negative only at the terminal (no early ok:true).
    let editor = visibleReplyEditor();
    let attempt = 0;
    while (!editor && attempt < 12) {
      // Try the expand trigger on EVERY attempt — the conversation section can
      // render a beat later than the rest of the page, so a single try races.
      // Idempotent: once expanded the collapsed bar is gone, this returns false.
      const justOpened = openReplyComposer();
      // Expanding re-renders/remounts the editor; inserting too soon lands in
      // the instance that gets wiped. Give a settle after each expand.
      await delay(justOpened ? 1100 : 300);
      editor = visibleReplyEditor();
      attempt += 1;
    }
    if (editor) {
      el = editor;
    } else if (!isField(el) || !isVisible(el)) {
      // Editor never appeared and the ref itself isn't a real, VISIBLE field —
      // fail honestly instead of typing into a hidden editor React will wipe.
      return { ok: false, label: "couldn't open the reply box" };
    }
  }
  if (!el) return { ok: false, label: "that field isn't on screen" };
  if (/\b(password|card|cvv|iban|account number)\b/i.test(labelFor(el) || ""))
    return refuse("that field");
  if (isEditable(el)) {
    if (v === "") {
      // Clearing a rich-text editor: use Quill's own API if available.
      const q = quillInstanceFor(el);
      if (q && typeof q.setText === "function") q.setText("");
      else el.textContent = "";
      return { ok: true, label: "cleared" };
    }
    insertIntoEditable(el, v);
    verifyEditableInsert(v);
    // Verify the text actually landed in the editor before claiming success.
    await rAF();
    await delay(50);
    const probe = v.slice(0, 40);
    const now = (el.innerText || el.textContent || "").trim();
    if (now.includes(probe)) return { ok: true, label: `typed “${value}”` };
    // One re-apply on miss.
    insertIntoEditable(el, v);
    await rAF();
    await delay(50);
    const retry = (el.innerText || el.textContent || "").trim();
    return retry.includes(probe)
      ? { ok: true, label: `typed “${value}”` }
      : { ok: false, label: "couldn't type into the reply box" };
  }
  // Plain input/textarea path.
  if (el.disabled || el.getAttribute("aria-disabled") === "true")
    return { ok: false, label: "that field is disabled" };
  try {
    el.focus();
  } catch {
    /* */
  }
  setReactValue(el, v);
  // Verify-after-act: React-controlled inputs can reject a programmatic set.
  await rAF();
  await delay(50);
  const landed = () => String(el.value ?? "");
  if (landed() === v || (v && landed().includes(v)))
    return { ok: true, label: `typed “${value}”` };
  // ONE re-apply on a miss.
  try {
    el.focus();
  } catch {
    /* */
  }
  setReactValue(el, v);
  await rAF();
  await delay(50);
  return landed() === v || (v && landed().includes(v))
    ? { ok: true, label: `typed “${value}”` }
    : { ok: false, label: "couldn't type into that field" };
}

// Find the current pagination bar (in <main> first, then anywhere).
function findPaginationBar() {
  return (
    document.querySelector('main [data-copilot="pagination-bar"]') ||
    document.querySelector('[data-copilot="pagination-bar"]')
  );
}

// Resolve as soon as a pagination bar appears (via MutationObserver on <main>),
// hard-capped so it never hangs. Returns the bar element or null.
function waitForPaginationBar(capMs = 3000) {
  return new Promise((resolve) => {
    const existing = findPaginationBar();
    if (existing) {
      resolve(existing);
      return;
    }
    let done = false;
    const finish = (bar) => {
      if (done) return;
      done = true;
      try {
        obs.disconnect();
      } catch {
        /* */
      }
      clearTimeout(timer);
      resolve(bar);
    };
    const root =
      document.querySelector("main, [data-scroll-root]") || document.body;
    const obs = new MutationObserver(() => {
      const bar = findPaginationBar();
      if (bar) finish(bar);
    });
    try {
      obs.observe(root, { childList: true, subtree: true });
    } catch {
      /* */
    }
    const timer = setTimeout(() => finish(findPaginationBar()), capMs);
  });
}

/** Go to a page number, or "next"/"prev". Walks the pager if N is outside the
 *  visible window. ASYNC: waits (MutationObserver, ~3s cap) for the pagination
 *  bar to mount after a fresh navigation, then resolves {ok,label} ONLY at the
 *  terminal state (the target page button actually clicked / a firm failure).
 *  No optimistic mid-retry ok:true. */
export async function goToPage(target) {
  const bar = await waitForPaginationBar(3000);
  if (!bar) {
    return {
      ok: false,
      label: "there's no pagination here (single page, or a different list)",
    };
  }
  const dir = String(target).toLowerCase();
  if (dir === "next" || dir === "prev") {
    const btn = bar.querySelector(
      `[aria-label="${dir === "next" ? "Next" : "Previous"} page"]`
    );
    if (!btn || btn.disabled)
      return {
        ok: false,
        label: `already on the ${dir === "next" ? "last" : "first"} page`,
      };
    btn.click();
    return { ok: true, label: dir === "next" ? "next page" : "previous page" };
  }
  const n = Number(target);
  if (!Number.isInteger(n) || n < 1)
    return { ok: false, label: "that's not a valid page number" };

  const direct = bar.querySelector(`[data-copilot-page="${n}"]`);
  if (direct) {
    direct.click();
    return { ok: true, label: `page ${n}` };
  }
  // N isn't in the visible window — is it even reachable?
  const nums = Array.from(bar.querySelectorAll("[data-copilot-page]")).map((b) =>
    Number(b.getAttribute("data-copilot-page"))
  );
  const totalPages = nums.length ? Math.max(...nums) : 1;
  if (n > totalPages)
    return { ok: false, label: `there is no page ${n} (only ${totalPages})` };

  // Walk toward N by comparing it to the CURRENTLY visible window (no reliance
  // on detecting the active page, which the first/last buttons don't mark).
  // Cap ~6 hops (~1.8s) so it resolves rather than walking forever.
  for (let steps = 0; steps < 6; steps += 1) {
    const liveBar = findPaginationBar();
    if (!liveBar) return { ok: false, label: `couldn't reach page ${n}` };
    const b = liveBar.querySelector(`[data-copilot-page="${n}"]`);
    if (b) {
      b.click();
      return { ok: true, label: `page ${n}` };
    }
    const visible = Array.from(liveBar.querySelectorAll("[data-copilot-page]"))
      .map((x) => Number(x.getAttribute("data-copilot-page")))
      .filter((x) => x > 1 && x < totalPages); // ignore the always-present 1/last
    const maxVis = visible.length ? Math.max(...visible) : 1;
    const navDir = n > maxVis ? "Next page" : "Previous page";
    const nav = liveBar.querySelector(`[aria-label="${navDir}"]`);
    if (!nav || nav.disabled) return { ok: false, label: `couldn't reach page ${n}` };
    nav.click();
    await delay(300);
  }
  return { ok: false, label: `couldn't reach page ${n}` };
}

// Dev-only test hook: lets the puppeteer harness drive the control functions
// deterministically (no LLM in the loop). Stripped from production builds.
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.__fkControls = {
    scanActionable,
    clickControl,
    setSelect,
    setValue,
    goToPage,
    forceClick,
    forceSet,
  };
}
